import React, { useContext, useEffect, useState } from "react";
import { StyleSheet, View, LayoutAnimation } from "react-native";

import ReflectionItemHeader from "./ReflectionItemHeader";
import ShowMoreItems from "../../common/ShowMoreItems";
import ReflectionContext from "../ReflectionContext";

import {
  hasItems,
  setStorageItem,
  getStorageItem,
  getStorageKey,
  isIterable,
} from "../../../../helpers/utils";

const MAX_NUM_ELEMENTS = 10;

let ReflectionItem = (props) => {
  let {
    item,
    parentType = "",
    index = -1,
    level = 1,
    controller = null,
    hasChart = false,
    reverse = true,
    source = null,
    expandAll = false,
  } = props;
  if (item == null) item = {};

  const { onElementPressed = () => {}, isReportMode } =
    useContext(ReflectionContext) || {};
  const collapsedStorageKey = getStorageKey(controller, "COLLAPSED_GROUPS");

  const [hasMoreItems, setHasMoreItems] = useState(level > 1);
  const [initialNumberToRender] = useState(MAX_NUM_ELEMENTS);
  const [itemsList, setItemsList] = useState([]);
  const [message, setMessage] = useState("");
  const [isCollapsed, setIsCollapsed] = useState(false);

  // 2022.04.29 Do not display elements in reflection for which there are 0 hours on the view and have no journal
  const hasValidHours = (days = []) => {
    let hasHours = false;
    days.forEach((d) => {
      if (d.hours > 0 || d.gem?.hours || d.gem?.mood) {
        hasHours = true;
      }
    });

    return days.length == 3 && hasHours;
  };

  const filterOutItems = (el) => (el && !el.isHidden && (isReportMode ? true : hasValidHours(el.days)));

  useEffect(() => {
    setIsCollapsed(!(expandAll || item.isExpanded || item.hasMatchedChildren));
  }, [expandAll, item.hasMatchedChildren]);

  useEffect(() => {
    const children = item.children;
    if (!children) return;

    if (hasMoreItems && children.length > initialNumberToRender) {
      setItemsList(
        children.slice(0, initialNumberToRender).filter(filterOutItems),
      );
      setHasMoreItems(true);
      setMessage(
        children.length -
          initialNumberToRender +
          ` more Activities & Tags in ${item.name}`,
      );
    } else {
      setItemsList([...children].filter(filterOutItems));
      setHasMoreItems(false);
    }
  }, [item.children]);

  // ------------------------------------- METHODS -------------------------------------
  const showMore = () => {
    const { children } = item;
    setItemsList(children.slice(0).filter(filterOutItems));
    setHasMoreItems(false);
  };

  const toggleExpand = () => {
    if (!hasItems(item.children)) {
      if (onElementPressed) onElementPressed(item);
      return;
    }

    setIsCollapsed((prevIsCollapsed) => {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);

      // For nested tags in multiple groups
      const elementId = [item.parent_id, item.item_id]
        .filter((id) => id)
        .join("-");

      // Keep track of collapsed groups
      getStorageItem(collapsedStorageKey).then((groupIds) => {
        let newGroupIds = new Set(
          isIterable(groupIds)
            ? groupIds
            : groupIds && groupIds.length > 0
            ? [groupIds.split(",")]
            : [],
        );
        if (!prevIsCollapsed) {
          newGroupIds.delete(elementId);
        } else {
          newGroupIds.add(elementId);
        }

        setStorageItem(collapsedStorageKey, newGroupIds);
      });

      setIsCollapsed(!prevIsCollapsed);
      item.isExpanded = !item.isExpanded;
    });
  };

  const isNotRendered =
    (source === "reflection" && item.name === "Hidden" && level === 1) ||
    (item.item_id === "other" &&
      (item.children == null || item.children.length == 0));

  return isNotRendered ? (
    <></>
  ) : (
    <>
      {/* For Search */}
      {item.isHidden ? null : (
        <View style={[styles.container, level == 1 && styles.set]}>
          <ReflectionItemHeader
            item={{ ...item }}
            index={index}
            onPress={toggleExpand}
            parentType={parentType}
            isCollapsed={isCollapsed && !expandAll}
            level={level}
            reverse={reverse}
            hasChart={hasChart}
          />

          {hasItems(itemsList) && !isCollapsed ? (
            <View style={styles.listContainer}>
              {itemsList.map((currentItem) => (
                <ReflectionItem
                  item={currentItem}
                  key={currentItem.item_id}
                  parentType={item.item_type}
                  level={level + 1}
                  controller={controller}
                  hasChart={hasChart}
                  reverse={reverse}
                  source={source}
                  expandAll={expandAll}
                />
              ))}
              <ShowMoreItems
                onPress={showMore}
                message={message}
                hasMoreItems={hasMoreItems}
              />
            </View>
          ) : null}
        </View>
      )}
    </>
  );
};

export default React.memo(ReflectionItem, (prev, next) => {
  return !!prev.isHidden && !!next.isHidden && prev.isHidden === next.isHidden;
});

const styles = StyleSheet.create({
  container: {
    position: "relative",
    backgroundColor: "#FFF",
  },
  set: {
    marginBottom: 8,
    borderRadius: 4,
    shadowColor: "#000",
    shadowOpacity: 0.1,
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 5,
  },
  moreWrapper: {
    display: "flex",
    justifyContent: "center",
    paddingLeft: 20,
    height: 50,
  },
  listContainer: {
    backgroundColor: "#FFF",
  },
});
